/**
 * Best seller carousel for index Best Sellers section (Swiper).
 */
export class LpBestSellerCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._swiperRaf = 0;
    this._swiperWaitStart = 0;
  }

  connectedCallback() {
    this.section = this.closest('.lp-index-best-seller');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');

    requestAnimationFrame(() => {
      this.dotsWrap = this.section?.querySelector('[data-dots]');
      this.initWhenReady();
    });

    // Fallback for the race below. Swiper is loaded with `defer` from
    // theme.liquid and does not land until ~2s on this storefront, well after
    // this element upgrades. The requestAnimationFrame wait in initWhenReady
    // returns permanently the first time the element is momentarily
    // disconnected — an app embed re-rendering an ancestor is enough — and
    // nothing re-arms it, so the carousel silently stayed a static full-width
    // stack on both the homepage and the FAQ page. `init` is idempotent (it
    // returns early once `swiper-initialized` is present), so this can only
    // ever recover a missed init, never double-initialise.
    this._onWindowLoad = () => this.initWhenReady();
    window.addEventListener('load', this._onWindowLoad, { once: true });
  }

  disconnectedCallback() {
    if (this._swiperRaf) {
      cancelAnimationFrame(this._swiperRaf);
    }

    if (this._onWindowLoad) {
      window.removeEventListener('load', this._onWindowLoad);
    }

    this.destroy();
  }

  initWhenReady() {
    if (window.Swiper) {
      this.init();
      return;
    }

    const maxWaitMs = 8000;
    this._swiperWaitStart = this._swiperWaitStart || performance.now();

    const waitForSwiper = () => {
      this._swiperRaf = 0;
      // Stop polling while detached, but leave _swiperWaitStart intact so a
      // reconnect or the window load fallback can pick the wait back up.
      if (!this.isConnected) return;
      if (window.Swiper) {
        this.init();
        return;
      }
      if (performance.now() - this._swiperWaitStart > maxWaitMs) {
        console.error('Swiper library not loaded');
        return;
      }
      this._swiperRaf = requestAnimationFrame(waitForSwiper);
    };

    this._swiperRaf = requestAnimationFrame(waitForSwiper);
  }

  getColumnSettings() {
    const styles = this.section ? getComputedStyle(this.section) : null;
    const desktop = parseInt(styles?.getPropertyValue('--columns-desktop')?.trim(), 10) || 4;
    return { desktop };
  }

  getPeek({ desktop = false } = {}) {
    // How much of the next card shows, as a fraction of one slide. Desktop
    // shows whole cards only, so no peek at any column count. Mobile and
    // tablet keep a sliver so the track reads as scrollable. The FAQ promo
    // band passes 0.103 in from Liquid, which is what Figma's 310.5px card at
    // a 330.5px pitch works out to.
    const declaredPeek = parseFloat(this.dataset.peek);
    if (!Number.isNaN(declaredPeek)) return declaredPeek;
    return desktop ? 0 : 0.15;
  }

  getSlidesPerView(columns, options) {
    return columns + this.getPeek(options);
  }

  init() {
    const swiperEl = this.querySelector('.lp-index-best-seller__swiper');
    if (!swiperEl || swiperEl.classList.contains('swiper-initialized')) return;

    if (!this.dotsWrap) {
      this.dotsWrap = this.section?.querySelector('[data-dots]');
    }

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length < 2) {
      this.setControlsHidden(true);
      return;
    }

    const { desktop: desktopCols } = this.getColumnSettings();
    const desktopPeek = this.getPeek({ desktop: true });
    const mobileCols = 1;
    const tabletCols = Math.min(2, desktopCols);

    const paginationLabel = this.dataset.paginationLabel;
    const paginationConfig = this.dotsWrap
      ? {
          el: this.dotsWrap,
          clickable: true,
          bulletClass: 'lp-index-best-seller__dot',
          bulletActiveClass: 'lp-index-best-seller__dot--active',
          renderBullet: (index, className) => {
            const label = paginationLabel
              ? paginationLabel.replace(/\[index\]/g, String(index + 1))
              : '';
            return label
              ? `<button type="button" class="${className}" aria-label="${label}"></button>`
              : `<button type="button" class="${className}" aria-label="Go to page ${index + 1}"></button>`;
          },
        }
      : false;

    // Gutter between cards. Defaults to the homepage's 16px; the FAQ page's
    // promo band is drawn at 20px in Figma and passes that in from Liquid.
    const gutter = parseInt(this.dataset.spaceBetween, 10) || 16;

    // Card layout (data-layout="auto"): slide widths come from CSS and the track
    // moves one card at a time, the same set-up as the Recently viewed carousel.
    if (this.isAutoLayout()) {
      this.swiper = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        spaceBetween: gutter,
        watchOverflow: true,
        grabCursor: true,
        a11y: {
          enabled: true,
          prevSlideMessage: this.prevBtn?.getAttribute('aria-label'),
          nextSlideMessage: this.nextBtn?.getAttribute('aria-label'),
        },
        navigation: {
          prevEl: this.prevBtn,
          nextEl: this.nextBtn,
        },
        pagination: paginationConfig,
        on: {
          init: () => requestAnimationFrame(() => this.syncControls()),
          resize: () => this.syncControls(),
        },
      });
      return;
    }

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: this.getSlidesPerView(mobileCols),
      slidesPerGroup: mobileCols,
      spaceBetween: 12,
      slidesOffsetAfter: this.getOffsetAfter(),
      watchOverflow: true,
      grabCursor: true,
      a11y: {
        enabled: true,
        prevSlideMessage: this.prevBtn?.getAttribute('aria-label'),
        nextSlideMessage: this.nextBtn?.getAttribute('aria-label'),
      },
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      },
      pagination: paginationConfig,
      breakpoints: {
        768: {
          slidesPerView: this.getSlidesPerView(tabletCols),
          slidesPerGroup: tabletCols,
          spaceBetween: gutter,
          slidesOffsetAfter: 40,
        },
        1024: {
          slidesPerView: this.getSlidesPerView(desktopCols, { desktop: true }),
          slidesPerGroup: desktopCols,
          spaceBetween: gutter,
          // Trailing offset lets a peeking card scroll clear of the edge. With
          // whole cards it would only leave dead space after the last one.
          slidesOffsetAfter: desktopPeek > 0 ? this.getOffsetAfter() : 0,
        },
      },
      on: {
        init: () => requestAnimationFrame(() => this.syncControls()),
        resize: () => this.syncControls(),
        breakpoint: () => this.syncControls(),
      },
    });
  }

  isAutoLayout() {
    return this.dataset.layout === 'auto';
  }

  getActiveSlidesPerGroup() {
    const width = window.innerWidth;
    const bp = this.swiper.params.breakpoints || {};

    if (width >= 1024) return bp[1024]?.slidesPerGroup ?? 3;
    if (width >= 768) return bp[768]?.slidesPerGroup ?? 2;
    return this.swiper.params.slidesPerGroup ?? 1;
  }

  needsPagination() {
    const slideCount = this.swiper.slides.length;
    const group = this.getActiveSlidesPerGroup();
    if (slideCount < 2) return false;
    return slideCount > group;
  }

  syncControls() {
    if (!this.swiper) return;

    if (this.isAutoLayout()) {
      this.swiper.update();
      const needsNav = this.swiper.slides.length > 1 && !this.swiper.isLocked;
      if (needsNav && this.swiper.pagination) {
        this.swiper.pagination.render();
        this.swiper.pagination.update();
      }
      this.setControlsHidden(!needsNav);
      return;
    }

    const needsNav = this.needsPagination();
    const width = window.innerWidth;
    const desktopPeek = this.getPeek({ desktop: true });
    const wantsOffset = needsNav && (width < 1024 || desktopPeek > 0);
    const offset = wantsOffset ? this.getOffsetAfter() : 0;

    this.swiper.params.slidesOffsetAfter = offset;
    if (width >= 1024 && this.swiper.params.breakpoints?.[1024]) {
      this.swiper.params.breakpoints[1024].slidesOffsetAfter = offset;
    } else if (width >= 768 && this.swiper.params.breakpoints?.[768]) {
      this.swiper.params.breakpoints[768].slidesOffsetAfter = needsNav ? 40 : 0;
    }

    this.swiper.update();

    if (this.swiper.pagination) {
      this.swiper.pagination.render();
      this.swiper.pagination.update();
    }

    this.setControlsHidden(!needsNav);
  }

  setControlsHidden(hidden) {
    const dotsInner = this.dotsWrap?.closest('.lp-index-best-seller__inner');

    if (this.dotsWrap) this.dotsWrap.hidden = hidden;
    if (dotsInner) dotsInner.hidden = hidden;
    if (this.prevBtn) this.prevBtn.hidden = hidden;
    if (this.nextBtn) this.nextBtn.hidden = hidden;
  }

  getOffsetAfter() {
    const width = window.innerWidth;

    if (width >= 1600) {
      const pad = Math.min(Math.max(70, width * 0.044), 120);
      return pad + Math.max(0, (width - 1300) / 2);
    }

    if (width >= 1024) {
      return 70 + Math.max(0, (width - 1300) / 2);
    }

    if (width >= 768) {
      return 40;
    }

    return 16;
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get('lp-best-seller-carousel')) {
  customElements.define('lp-best-seller-carousel', LpBestSellerCarousel);
}
